import * as React from "react";
import * as ReactDOM from "react-dom";
import { ProgressIndicator } from "OfficeFabric/ProgressIndicator";
import { delay, DelayedFunction } from "VSS/Utils/Core";
import * as WITResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";

export interface IProgressBarProps {
    label: string,
    description: string,
    percentComplete: number,
};

export interface IProgressBarState {
    label: string,
    description: string,
    percentComplete: number,
};

class ProgressBar extends React.Component<IProgressBarProps, IProgressBarState> {
    constructor(props) {
        super(props);

        this.state = {
            label: props.label,
            description: props.description,
            percentComplete: props.percentComplete,
        }
    }

    public update(progressBarData: IProgressBarProps) {
        this.setState({ label: progressBarData.label, description: progressBarData.description, percentComplete: progressBarData.percentComplete });
    }

    public render(): JSX.Element {
        return (
            <div className="progress-indicator-overlay">
                <ProgressIndicator className="progress-indicator" label={this.state.label} description={this.state.description} percentComplete={this.state.percentComplete} barHeight={6} />
            </div>
        );
    }
}

export interface IProgressBarOverlayData {
    label: string;
    current: number;
    total: number;
    increment: number;
};

export class ProgressBarOverlay {
    private _element: HTMLDivElement;

    private _delayedShowFunction: DelayedFunction;
    private _delayedHideFunction: DelayedFunction;

    private _label: string;
    private _current: number;
    private _total: number;
    private _increment: number;
    private _progressBar: any; // many types

    public constructor(private _referenceElement: HTMLElement, progressBarData: IProgressBarOverlayData) {
        this._label = progressBarData.label;
        this._current = progressBarData.current;
        this._total = progressBarData.total;
        this._increment = progressBarData.increment;
    }

    public dispose() {
        if (this._delayedShowFunction) {
            this._delayedShowFunction.cancel();
        }

        if (this._delayedHideFunction) {
            this._delayedHideFunction.cancel();
        }
        this._hideProgressBarOverlay();
    }

    public show(waitTime: number, loadingText?: string) {
        if (this._delayedShowFunction) {
            this._delayedShowFunction.cancel();
        }

        if (this._delayedHideFunction) {
            this._delayedHideFunction.cancel();
        }

        this._delayedShowFunction = delay(this, waitTime, this._showProgressBarOverlay, [loadingText || WITResources.ProgressBarLoading]);
    }

    public hide(waitTime?: number) {
        if (this._delayedShowFunction) {
            this._delayedShowFunction.cancel();
        }

        if (this._delayedHideFunction) {
            this._delayedHideFunction.cancel();
        }

        if (waitTime === undefined || waitTime === null || waitTime < 0) {
            this._hideProgressBarOverlay();
        } else {
            this._delayedHideFunction = delay(this, waitTime, this._hideProgressBarOverlay);
        }
    }

	public increment(increment?: number) {
		const newCurrent = this._current + (increment && increment >= 0 ? increment : this._increment);
		this._current = Math.min(newCurrent, this._total);

		if (this._progressBar) {
            this._progressBar.update({ label: this._label, description: this._getDescription(), percentComplete: this._getPercentComplete() });
        }
	}

    private _getDescription() {
		const beginningOfRange = Math.min(this._current + 1, this._total);
		const endOfRange = Math.min(this._current + this._increment, this._total);
        return `${this._label} ${beginningOfRange} - ${endOfRange} of ${this._total}.`;
    }

    private _getPercentComplete() {
        return this._current / this._total;
    }

    private _showProgressBarOverlay() {
        if (!this._element) {
            this._element = document.createElement("div");
        }

        const label = this._label;
        const description = this._getDescription();
        const percentComplete = this._getPercentComplete();

        this._progressBar = ReactDOM.render(
            <ProgressBar label={label} description={description} percentComplete={percentComplete} />,
            this._element
        );

        const parent = this._referenceElement.parentElement;
        parent.insertBefore(this._element, this._referenceElement);
    }

    private _hideProgressBarOverlay() {
        if (this._element) {
            ReactDOM.unmountComponentAtNode(this._element);
            this._element.parentNode.removeChild(this._element);
            this._element = null;
        }
    }
}